import { createContext, useContext } from "react";
import { useParams } from "@tanstack/react-router";

export type TeamRouteIdentity = {
  teamId: string;
  teamSlug: string;
};

const TeamRouteIdentityContext = createContext<TeamRouteIdentity | null>(null);

export const TeamRouteIdentityProvider = TeamRouteIdentityContext.Provider;

/** Soft read of the Team layout identity (null outside `/teams/$teamId`). */
export function useOptionalTeamRouteIdentity(): TeamRouteIdentity | null {
  return useContext(TeamRouteIdentityContext);
}

/** Immutable Team identity + browser slug from the LAT-1501 Team layout. */
export function useTeamRouteIdentity(): TeamRouteIdentity {
  const value = useOptionalTeamRouteIdentity();
  if (!value) {
    throw new Error("useTeamRouteIdentity requires the Team route layout");
  }
  return value;
}

/**
 * Leaf-route identity reader. Prefer the layout provider (immutable id +
 * slug). When the provider is absent — route unit tests that only mock
 * `createFileRoute` — fall back to the physical `$teamId` param (the browser
 * slug after LAT-1501) as both fields so leaves still mount.
 */
function useTeamRouteIdentityOrRouteParamFallback(): TeamRouteIdentity {
  const value = useOptionalTeamRouteIdentity();
  // Always call useParams so hook order is stable when the provider is present.
  let routeTeamParam: string | undefined;
  try {
    const params = useParams({ strict: false }) as { teamId?: string };
    routeTeamParam =
      typeof params.teamId === "string" && params.teamId.length > 0
        ? params.teamId
        : undefined;
  } catch {
    routeTeamParam = undefined;
  }
  if (value) return value;
  if (routeTeamParam) {
    return { teamId: routeTeamParam, teamSlug: routeTeamParam };
  }
  throw new Error("useTeamRouteIdentity requires the Team route layout");
}

/** Drop-in for prior getRouteApi(...).useLoaderData() call sites. */
export const teamRouteApi = {
  useLoaderData: useTeamRouteIdentityOrRouteParamFallback,
};
